import type Keycloak from 'keycloak-js';
import type { FetchResponse } from 'ofetch';
import type { FetchError } from 'ofetch';
import type { NitroFetchOptions } from 'nitropack';

interface GenericErrorResponse {
  error: string;
  message: string;
}

export const SUCCESS = 'success';

// use .catch((error) => error.data); to handle error
export async function useCustomFetch<T>(url: string, options: NitroFetchOptions<string> = {}): Promise<T> {
  return $fetch(url, await getDefaultOptions(options, useJwt(url)));
}

// use this for Keycloak calls (call keyclaok api with token)
export async function useCustomFetchForKeycloakCalls<T>(
  url: string,
  options: NitroFetchOptions<string> = {}
): Promise<T> {
  return $fetch(url, await getDefaultOptionsForKeycloakCalls(options));
}

// return a native response like in js fetch
export async function useRawCustomFetch<T>(
  url: string,
  options: NitroFetchOptions<string> = {}
): Promise<FetchResponse<T>> {
  return $fetch.raw(url, await getDefaultOptions(options, useJwt(url)));
}

// to use the "useFetch" function instead of "$fetch"
export async function useCustomFetchApi<T>(
  url: string | (() => string),
  options: Parameters<typeof useFetch<T>>[1] = {}
): Promise<ReturnType<typeof useFetch<T>>> {
  const finalUrl = typeof url === 'string' ? url : '';
  const finalOptions = await getDefaultUseFetchOptions(options, useJwt(finalUrl));
  return useFetch<T, FetchError<GenericErrorResponse>>(url, {
    ...finalOptions,
  });
}

function useJwt(url: string): boolean {
  const config = useRuntimeConfig();
  let test = false;
  config.public.useJwtToCallMs.forEach((urlMs) => {
    if (url.includes(urlMs)) {
      test = true;
    }
  });

  return test;
}

export async function getAuthValue(useTokenFromConfig: boolean): Promise<string> {
  let authValue: string = '';
  const { keycloak } = useKeycloak();
  const token = await getToken(keycloak);

  if (useTokenFromConfig) {
    if (!token) {
      console.error('Error RequestInit : token is empty');
    } else {
      authValue = `Bearer ${token}`;
    }
  } else {
    const store = useFloristInfoStore();
    let basic;
    if (store.basic) {
      basic = store.basic;
    } else {
      basic = keycloak.tokenParsed?.x_jwt_fasterize_b as string | undefined;
      store.basic = basic ?? '';
    }
    authValue = `Basic ${basic}`;
  }

  return authValue;
}

async function getToken(keycloak: Keycloak): Promise<string> {
  const minimumTokenValiditySeconds = 30;

  if (keycloak) {
    if (keycloak.isTokenExpired()) {
      await keycloak.updateToken(minimumTokenValiditySeconds);
      if (typeof keycloak.token === 'undefined') {
        return '';
      }
      return keycloak.token;
    } else {
      if (typeof keycloak.token === 'undefined') {
        return '';
      }
      return keycloak.token;
    }
  } else {
    return '';
  }
}

async function getDefaultOptions(
  options: NitroFetchOptions<string>,
  useJwt: boolean
): Promise<NitroFetchOptions<string>> {
  const config = useRuntimeConfig();
  const authValue = await getAuthValue(useJwt);
  return {
    timeout: config.public.fetchGlobalTimeout,
    ...options,
    headers: {
      Authorization: `${authValue}`,
      ...options?.headers,
    },
  };
}

async function getDefaultOptionsForKeycloakCalls(
  options: NitroFetchOptions<string>
): Promise<NitroFetchOptions<string>> {
  const config = useRuntimeConfig();
  const { keycloak } = useKeycloak();
  const token = await getToken(keycloak);
  let authValue = '';

  if (!token) {
    console.error('Error RequestInit : token is empty');
  } else {
    authValue = `Bearer ${token}`;
  }
  return {
    timeout: config.public.fetchGlobalTimeout,
    ...options,
    headers: {
      Authorization: `${authValue}`,
      ...options?.headers,
    },
  };
}

async function getDefaultUseFetchOptions<T>(options: Parameters<typeof useFetch<T>>[1], useJwt: boolean) {
  const config = useRuntimeConfig();
  const authValue = await getAuthValue(useJwt);
  return {
    timeout: config.public.fetchGlobalTimeout,
    ...options,
    headers: {
      Authorization: `${authValue}`,
      ...options?.headers,
    },
  };
}
